import { useState } from 'react';
import {
  FileText,
  Download,
  Sliders,
  ArrowRight,
  Calculator,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

interface MeasurementPoint {
  cml: string;
  measuredThickness: number;
  nominalThickness: number;
  previousThickness: number;
  corrosionRateMmYr: number;
}

interface ExtractedRecord {
  assetTag: string;
  location: string;
  reportDate: string;
  ndtMethod: string;
  inspector: string;
  confidence: number;
  measurements: MeasurementPoint[];
}

const SAMPLE_REPORTS = [
  {
    name: 'Sample 1: Turnaround UT Thickness Survey (Form API 570)',
    date: '1998-11-14',
    pages: 8,
    records: [
      {
        assetTag: '12"-HC-2019-CS',
        location: 'Unit 3 - Hydrocracker Reactor Feed',
        reportDate: '1998-11-14',
        ndtMethod: 'Ultrasonic Contact (UT)',
        inspector: 'H. Van Der Berg (API 570 Cert #4412)',
        confidence: 0.94,
        measurements: [
          { cml: 'CML-01 (Elbow Ext)', measuredThickness: 11.2, nominalThickness: 12.7, previousThickness: 12.5, corrosionRateMmYr: 0.13 },
          { cml: 'CML-02 (Elbow Int)', measuredThickness: 10.4, nominalThickness: 12.7, previousThickness: 12.1, corrosionRateMmYr: 0.17 },
          { cml: 'CML-03 (Low Point)', measuredThickness: 8.9, nominalThickness: 12.7, previousThickness: 11.4, corrosionRateMmYr: 0.25 },
        ],
      },
      {
        assetTag: 'TK-402A-SHELL',
        location: 'Storage Tank Farm Course 2',
        reportDate: '1998-11-15',
        ndtMethod: 'UT Grid',
        inspector: 'M. Al-Faraj',
        confidence: 0.91,
        measurements: [
          { cml: 'SHELL-G1', measuredThickness: 13.8, nominalThickness: 15.0, previousThickness: 14.5, corrosionRateMmYr: 0.07 },
          { cml: 'SHELL-G2', measuredThickness: 13.4, nominalThickness: 15.0, previousThickness: 14.2, corrosionRateMmYr: 0.08 },
        ],
      },
    ],
  },
  {
    name: 'Sample 2: CUI NDT Inspection Dossier (2014)',
    date: '2014-05-22',
    pages: 14,
    records: [
      {
        assetTag: '06"-CDU-1044-A106B',
        location: 'Atmospheric Distillation Bottoms',
        reportDate: '2014-05-22',
        ndtMethod: 'Pulsed Eddy Current (PEC)',
        inspector: 'R. Jensen (Level III NDT)',
        confidence: 0.96,
        measurements: [
          { cml: 'DRAIN-01', measuredThickness: 5.6, nominalThickness: 8.56, previousThickness: 7.2, corrosionRateMmYr: 0.20 },
          { cml: 'SHOE-01', measuredThickness: 6.1, nominalThickness: 8.56, previousThickness: 7.8, corrosionRateMmYr: 0.21 },
        ],
      },
    ],
  },
];

export function PDFParserPage() {
  const [selectedSample, setSelectedSample] = useState(SAMPLE_REPORTS[0]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeRecords, setActiveRecords] = useState<ExtractedRecord[]>(SAMPLE_REPORTS[0].records);

  const handleSelectSample = (sample: typeof SAMPLE_REPORTS[0]) => {
    setSelectedSample(sample);
    setActiveRecords(sample.records);
    toast.success(`Loaded ${sample.name}`);
  };

  const handleParse = async () => {
    setIsLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setActiveRecords(selectedSample.records);
      toast.success('Extracted 5 CML thickness rows and OCR metadata');
    } catch {
      toast.error('Failed to parse document');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="telemetry-pill bg-rose-50 text-rose-700 font-bold border border-rose-200">
                DOCUMENT INTELLIGENCE OCR & LLM PARSER
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight flex items-center gap-2.5">
              <FileText className="h-7 w-7 text-rose-600" />
              Legacy PDF Report Digitization
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Extract historical ultrasonic thickness readings, corrosion rates, and inspector notes from decades of scanned paper and PDF inspection files.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Link to="/tools/ffs" className="btn btn-secondary text-xs sm:text-sm py-2 px-3 shadow-sm">
              <Calculator className="h-4 w-4 text-blue-600" />
              <span>Evaluate FFS</span>
            </Link>
            <button
              onClick={() => toast.success('Exporting structured CSV dataset')}
              className="btn btn-primary text-xs sm:text-sm py-2 px-3 shadow-sm flex items-center gap-1.5"
            >
              <Download className="h-4 w-4" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>
      </div>

      {/* Preset Documents Bar */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-xs font-mono font-bold text-slate-700">
          <Sliders className="h-4 w-4 text-rose-600" />
          <span>LOAD SAMPLE ARCHIVAL REPORT:</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {SAMPLE_REPORTS.map((s, idx) => (
            <button
              key={idx}
              onClick={() => handleSelectSample(s)}
              className={`btn text-xs py-1.5 px-3 rounded-lg font-medium transition-all ${
                selectedSample.name === s.name
                  ? 'btn-primary shadow-sm'
                  : 'btn-secondary text-slate-700 hover:bg-white'
              }`}
            >
              {s.name}
            </button>
          ))}
        </div>
      </div>

      {/* Main Workspace */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Left Column: Document Scanner Preview */}
        <div className="lg:col-span-5 space-y-4">
          <div className="card bg-slate-900 text-white rounded-xl overflow-hidden shadow-xl border border-slate-800 p-5 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <span className="font-mono text-xs text-slate-300 font-bold">
                SCANNED ARCHIVE VIEW // {selectedSample.pages} PAGES
              </span>
              <span className="telemetry-pill bg-rose-900/60 text-rose-300 border border-rose-700">
                OCR ENGINE ACTIVE
              </span>
            </div>

            {/* Simulated Scanned Page Frame */}
            <div className="bg-slate-950 p-4 rounded-lg border border-slate-700 font-mono text-[11px] space-y-2 text-slate-400">
              <div className="border-b border-dashed border-slate-800 pb-2 text-slate-200 font-bold flex justify-between">
                <span>INSPECTION RECORD FORM 570</span>
                <span>DATE: {selectedSample.date}</span>
              </div>
              <div className="p-2 bg-slate-900 rounded border border-slate-800 text-slate-300">
                <span className="text-yellow-400 font-bold">OCR DETECTED TABLE:</span>
                <p className="mt-1">CIRCUIT: 12"-HC-2019-CS // SCH 80 CARBON STEEL</p>
                <p>NOMINAL: 12.7mm // MIN DETECTED: 8.9mm (CML-03)</p>
              </div>
              <div className="p-2 bg-slate-900 rounded border border-slate-800 text-slate-400 italic">
                "Note: External jacketing shows water staining at 6 o'clock position. Recommend follow up at next turnaround."
              </div>
            </div>

            <div className="pt-2">
              <button
                onClick={handleParse}
                disabled={isLoading}
                className="btn btn-primary w-full py-3 text-sm font-semibold shadow-md flex items-center justify-center gap-2"
              >
                <FileText className="h-4 w-4" />
                <span>{isLoading ? 'Executing LayoutLM & Table Extraction...' : 'Re-Run Table Extraction'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: Extracted Structured Tables */}
        <div className="lg:col-span-7 space-y-6">
          <div className="card bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="font-bold text-slate-900 text-base">Extracted Thickness Circuits</h3>
                <p className="text-xs text-slate-500">
                  {activeRecords.length} functional locations digitized into SQL database
                </p>
              </div>
              <span className="telemetry-pill bg-green-50 text-green-700 font-bold border border-green-200">
                100% PARSED
              </span>
            </div>

            {/* Extracted Record Cards */}
            <div className="space-y-4">
              {activeRecords.map((rec, rIdx) => (
                <div key={rIdx} className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="font-mono font-bold text-slate-900 text-sm">{rec.assetTag}</div>
                      <div className="text-xs text-slate-500 font-sans">{rec.location}</div>
                    </div>
                    <div className="flex items-center gap-2 font-mono text-xs">
                      <span className="telemetry-pill bg-white text-slate-700 border border-slate-200">
                        {rec.ndtMethod}
                      </span>
                      <span className="text-green-600 font-bold">{(rec.confidence * 100).toFixed(0)}% Match</span>
                    </div>
                  </div>

                  {/* Measurement Points Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-left font-mono text-xs">
                      <thead>
                        <tr className="text-slate-500 border-b border-slate-200">
                          <th className="pb-1.5">LOCATION (CML)</th>
                          <th className="pb-1.5">ACTUAL (mm)</th>
                          <th className="pb-1.5">NOMINAL</th>
                          <th className="pb-1.5">CORROSION RATE</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {rec.measurements.map((m, mIdx) => (
                          <tr key={mIdx} className="py-1">
                            <td className="py-1.5 text-slate-800 font-semibold">{m.cml}</td>
                            <td className="py-1.5 font-bold text-slate-900">
                              <span className={m.measuredThickness < m.nominalThickness * 0.75 ? 'text-red-600' : 'text-slate-800'}>
                                {m.measuredThickness} mm
                              </span>
                            </td>
                            <td className="py-1.5 text-slate-500">{m.nominalThickness} mm</td>
                            <td className="py-1.5 text-amber-700 font-bold">{m.corrosionRateMmYr} mm/yr</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="pt-2 border-t border-slate-200 flex items-center justify-between text-xs">
                    <span className="text-slate-500 font-sans">Inspector: {rec.inspector}</span>
                    <Link to="/tools/ffs" className="text-blue-600 hover:underline font-semibold flex items-center gap-1">
                      <span>Import into API 579 FFS</span>
                      <ArrowRight className="h-3 w-3" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
